/**
 * Declarative configs of the vehicle « fiche » cards (`EntityInfoCard`
 * usages): stable field keys + French labels (design order) and the mappers
 * from the service view models to the cards' `values`.
 *
 * - « Détails » (fiche 22809): `immatriculation`, `marque`, `modele`,
 *   `miseEnCirculation`, `conducteur`, `dateEntree`, `statut` (badge).
 * - « Contrat » (fiche 22811): `numero`, `produit`, `assureur`, `assure`,
 *   `dateEffet`, `dateEcheance`, `primeAnnuelle` (€), `fractionnement`, and
 *   the « Voir le contrat » action (stable key `viewContract`).
 */

import type { VehicleContract, VehicleDetails } from '../../api/fleetService';
import { formatEur } from '../../utils/format';
import type {
  EntityInfoCardAction,
  EntityInfoField,
  EntityInfoValue,
} from '../shared/EntityInfoCard';
import { VEHICLE_STATUS_BADGE } from './vehicleStatus';

/** Stable keys of the « Détails » card. */
export type VehicleDetailsFieldKey =
  | 'immatriculation'
  | 'marque'
  | 'modele'
  | 'miseEnCirculation'
  | 'conducteur'
  | 'dateEntree'
  | 'statut';

export const VEHICLE_DETAILS_FIELDS: readonly (EntityInfoField & { key: VehicleDetailsFieldKey })[] =
  [
    { key: 'immatriculation', label: 'Immatriculation' },
    { key: 'marque', label: 'Marque' },
    { key: 'modele', label: 'Modèle' },
    { key: 'miseEnCirculation', label: 'Mise en circulation' },
    { key: 'conducteur', label: 'Conducteur principal' },
    { key: 'dateEntree', label: "Date d'entrée" },
    { key: 'statut', label: 'Statut', badge: VEHICLE_STATUS_BADGE },
  ];

/** `values` of the « Détails » card; `null` record ⇒ `null` (empty state). */
export function vehicleDetailsValues(
  details: VehicleDetails | null,
): Record<VehicleDetailsFieldKey, EntityInfoValue> | null {
  if (details === null) return null;
  return {
    immatriculation: details.immatriculation,
    marque: details.marque,
    modele: details.modele,
    miseEnCirculation: details.miseEnCirculation,
    conducteur: details.conducteur,
    dateEntree: details.dateEntree,
    statut: details.statut,
  };
}

/** Stable keys of the « Contrat » card. */
export type VehicleContractFieldKey =
  | 'numero'
  | 'produit'
  | 'assureur'
  | 'assure'
  | 'dateEffet'
  | 'dateEcheance'
  | 'primeAnnuelle'
  | 'fractionnement';

export const VEHICLE_CONTRACT_FIELDS: readonly (EntityInfoField & {
  key: VehicleContractFieldKey;
})[] = [
  { key: 'numero', label: 'N° de contrat' },
  { key: 'produit', label: 'Produit' },
  { key: 'assureur', label: 'Assureur' },
  { key: 'assure', label: 'Assuré' },
  { key: 'dateEffet', label: "Date d'effet" },
  { key: 'dateEcheance', label: "Date d'échéance" },
  { key: 'primeAnnuelle', label: 'Prime annuelle' },
  { key: 'fractionnement', label: 'Fractionnement' },
];

/** `values` of the « Contrat » card (prime in `1 800,00 €`); `null` ⇒ `null`. */
export function vehicleContractValues(
  contract: VehicleContract | null,
): Record<VehicleContractFieldKey, EntityInfoValue> | null {
  if (contract === null) return null;
  return {
    numero: contract.numero,
    produit: contract.produit,
    assureur: contract.assureur,
    assure: contract.assure,
    dateEffet: contract.dateEffet,
    dateEcheance: contract.dateEcheance,
    primeAnnuelle: contract.primeAnnuelle === null ? null : formatEur(contract.primeAnnuelle),
    fractionnement: contract.fractionnement,
  };
}

/** Stable key of the « Voir le contrat » action (`actions` bucket). */
export const VIEW_CONTRACT_ACTION_KEY = 'viewContract';

/** The « Voir le contrat » action, pointing at `to`. */
export function viewContractAction(to: string): EntityInfoCardAction {
  return { key: VIEW_CONTRACT_ACTION_KEY, label: 'Voir le contrat', to };
}
